import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { CONVERSATION_EXPORT_POLL_TIMEOUT_MS } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  ExportDownloadPayload,
  ExportMode,
  Extension,
  METADATA_FILTER_KEY_MAX_CHARS,
  METADATA_FILTER_VALUE_MAX_CHARS,
  supportsMetadataFilter,
  UrlToFile,
} from '@/Domain/api/Export/export.dto';

const createExportAnalyticsSchema = (metadataValueRequiredMessage: string) =>
  z
    .object({
      start_date: z.date(),
      end_date: z.date(),
      extension: z.nativeEnum(Extension),
      query: z.string().optional(),
      metadata_key: z.string().max(METADATA_FILTER_KEY_MAX_CHARS).optional(),
      metadata_value: z.string().max(METADATA_FILTER_VALUE_MAX_CHARS).optional(),
      send_email_notification: z.boolean(),
    })
    .refine((data) => data.start_date <= data.end_date, {
      path: ['end_date'],
      message: 'End date must be greater than or equal to start date',
    })
    .superRefine((data, context) => {
      if (data.metadata_key && !data.metadata_value?.trim()) {
        context.addIssue({
          code: 'custom',
          path: ['metadata_value'],
          message: metadataValueRequiredMessage,
        });
      }
    });

export type ExportAnalyticsForm = z.infer<ReturnType<typeof createExportAnalyticsSchema>>;

const exportModeToMutationKey: Record<ExportMode, readonly unknown[]> = {
  [ExportMode.CONVERSATIONS]: [QueryKey.export.conversations],
  [ExportMode.DATA_GATHERING]: [QueryKey.export.dataGathering],
  [ExportMode.LINK_TRACKING]: [QueryKey.export.linkTracking],
  [ExportMode.AI_INSIGHTS]: [QueryKey.export.aiInsights],
  [ExportMode.TRIAGE_DATA]: [QueryKey.export.triageData],
};

export const useExportAnalyticsForm = (exportMode: ExportMode) => {
  const { t } = useTranslation('inbox');
  const formatDateForApi = (date: Date): string => format(date, 'yyyy-MM-dd');

  const today = new Date();
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);

  const form = useForm<ExportAnalyticsForm>({
    mode: 'onChange',
    defaultValues: {
      start_date: lastWeek,
      end_date: today,
      extension: Extension.CSV,
      send_email_notification: true,
    },
    resolver: zodResolver(createExportAnalyticsSchema(t('export.form.metadata_value_required'))),
  });
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query') || undefined;

  const mutationKey = exportModeToMutationKey[exportMode];

  const downloadMutation = useMutation<UrlToFile, unknown, ExportDownloadPayload>({
    mutationKey,
  });

  const handleSubmit = async (data: ExportAnalyticsForm) => {
    if (downloadMutation.isPending) {
      return;
    }
    const metadataValue = data.metadata_value?.trim();
    const submitData: ExportDownloadPayload = {
      start_date: formatDateForApi(data.start_date),
      end_date: formatDateForApi(data.end_date),
      extension: data.extension,
      send_email_notification: data.send_email_notification,
      query,
      ...(supportsMetadataFilter(exportMode) && data.metadata_key && metadataValue
        ? {
            metadata_key: data.metadata_key,
            metadata_value: metadataValue,
          }
        : {}),
    };

    const loading_message = `${t('export.toast_loading')} ${formatDateForApi(
      data.start_date,
    )} to ${formatDateForApi(data.end_date)}`;
    const toastId = toast.loading(loading_message, {
      duration: CONVERSATION_EXPORT_POLL_TIMEOUT_MS + 60_000,
    });
    try {
      await downloadMutation.mutateAsync(submitData);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('[ExportAnalyticsForm] mutation failed', error);
      toast.dismiss(toastId);
    }
  };

  return { form, handleSubmit, isPending: downloadMutation.isPending };
};
